import { clientRect } from "@/utils/client-rect"

export default function (element, dataset) {
  const icons = gsap.utils.toArray('.logo-hero_icon')
  const totalIcons = icons.length
  const container = clientRect(element.closest('.u-container'))

  // Mobile scale: reduce coordinates by 30% on screens 767px and below
  const isMobile = window.innerWidth <= 767
  const scale = isMobile ? 0.7 : 1

  const radius = container.width / (isMobile ? 4 : 5.5) // larger radius on mobile
  const startAngle = -Math.PI / 2 // start from 12 o'clock

  // Helper to get angle for circular formation
  const getCircleAngle = (index) => startAngle + (index / totalIcons) * Math.PI * 2

  // Fixed scattered positions - 2 rows, 3 icons each (scaled for mobile)
  const scatteredPositions = [
    // Top row
    { x: -200 * scale, y: -180 * scale },  // top-left
    { x: 50 * scale, y: -220 * scale },    // top-center
    { x: 280 * scale, y: -80 * scale },    // top-right
    // Bottom row
    { x: -180 * scale, y: 180 * scale },   // bottom-left
    { x: 20 * scale, y: 50 * scale },      // bottom-center
    { x: 220 * scale, y: 230 * scale },    // bottom-right
  ]

  const scatteredData = icons.map((_, i) => ({
    x: scatteredPositions[i % scatteredPositions.length].x,
    y: scatteredPositions[i % scatteredPositions.length].y,
    rotation: gsap.utils.random(0, 360),
    velocityX: gsap.utils.random(-30, 30) * scale,
    velocityY: gsap.utils.random(-30, 30) * scale,
  }))

  // Set initial scattered positions
  gsap.set(icons, {
    xPercent: -50,
    yPercent: 50,
    x: (i) => scatteredData[i].x,
    y: (i) => scatteredData[i].y,
    rotation: (i) => scatteredData[i].rotation,
    opacity: 1,
  })

  // Factory: Scattered drift animation
  function createScatteredDrift() {
    const tl = gsap.timeline()
    icons.forEach((icon, i) => {
      tl.to(icon, {
        opacity: 1,
        x: `+=${scatteredData[i].velocityX}`,
        y: `+=${scatteredData[i].velocityY}`,
        rotation: `+=${gsap.utils.random(-20, 20)}`,
        duration: 2,
        ease: "none",
      }, 0)
    })
    return tl
  }

  // Factory: Move into circular formation
  function createCircleFormation() {
    return gsap.timeline()
      .to(icons, {
        x: (i) => Math.cos(getCircleAngle(i)) * radius,
        y: (i) => Math.sin(getCircleAngle(i)) * radius,
        rotation: (i) => {
          const angle = getCircleAngle(i)
          return (angle * 180) / Math.PI + 30
        },
        duration: 2,
        ease: "power2.inOut",
        stagger: {
          each: 0.05,
          from: "random",
        },
      })
  }

  // Factory: Rotate circle while icons peel off one by one to line
  function createRotateAndPeelToLine() {
    const tl = gsap.timeline()
    const rotation = { angle: 0 }
    const leftCircle = new Set() // track which icons have left
    const spacing = isMobile ? 80 : 200
    const lineStartX = -((totalIcons - 1) * spacing) / 4
    const peelDelay = 0.9 // time between each icon leaving
    const peelDuration = 1.4 // how long each icon takes to reach line

    // Continuous rotation that only affects icons still in circle
    const rotationTween = gsap.to(rotation, {
      angle: Math.PI * 2, // full rotation available
      duration: totalIcons * peelDelay + 1, // enough time for all icons to leave
      ease: "none",
      onStart: () => {
        // Reset state for each loop
        rotation.angle = 0
        leftCircle.clear()
      },
      onUpdate: () => {
        icons.forEach((icon, i) => {
          if (leftCircle.has(i)) return // skip icons that left
          const baseAngle = getCircleAngle(i)
          const currentAngle = baseAngle + rotation.angle
          gsap.set(icon, {
            x: Math.cos(currentAngle) * radius,
            y: Math.sin(currentAngle) * radius,
            rotation: (currentAngle * 180) / Math.PI + 30,
          })
        })
      },
    })

    tl.add(rotationTween, 0)
    tl.to('body', { opacity: 1, duration: 1 })
    // Peel each icon off to its line position (anti-clockwise)
    const initialRotationTime = 1.8 // rotate before peeling starts
    icons.forEach((icon, i) => {
      tl.to(icon, {
        x: lineStartX + i * spacing,
        y: 0,
        rotation: 360,
        duration: peelDuration,
        ease: "power1.out",
        onStart: () => leftCircle.add(i),
      }, initialRotationTime + (totalIcons - 1 - i) * peelDelay)
    })

    return tl
  }

  // Factory: Exit right off screen
  function createExitRight() {
    return gsap.timeline()
      .to(icons, {
        x: `+=${container.ww}`,
        duration: 1.5,
        ease: "power1.in",
        stagger: {
          each: 0.05,
          from: "end",
        },
      })
  }

  // Factory: Stagger fade out
  function createFadeOut() {
    return gsap.timeline()
      .to(icons, {
        opacity: 0,
        duration: 0.3,
        scale: 0.8,
        stagger: {
          each: 0.05,
          from: "start",
        },
      })
  }

  // Factory: Reset to scattered positions
  function createReset() {
    let tl = gsap.timeline()
    tl.set(icons, {
      x: (i) => scatteredData[i].x,
      y: (i) => scatteredData[i].y,
      rotation: (i) => scatteredData[i].rotation,
    })
    tl.to(icons, {
      opacity: 1,
      scale: 1
    })
    return tl

  }

  // Master timeline
  const masterTl = gsap.timeline({
    repeat: -1,
    repeatDelay: 0.5,
  })

  masterTl
    .add(createScatteredDrift())
    .add(createCircleFormation())
    .add(createRotateAndPeelToLine())
    .add(createExitRight())
    .add(createFadeOut())
    .add(createReset())

  return masterTl
}

